import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { loadPdfjs } from '../utils/pdfjsRuntime';
import { SIGN_ZOOM_STEPS, signRenderCeiling, signRenderScale, stepSignZoom } from '../utils/signZoom';
import {
  placementViewScale, newFieldBox, movedFieldBox, resizedFieldBox,
} from '../utils/placementGeometry';
import { authedFetch } from '../lib/authedFetch';

const ONBOARDING_FIELD_TYPES = [
  { type: 'signature', label: 'Signature', defW: 170, defH: 42 },
  { type: 'text', label: 'Typed Name', defW: 180, defH: 24 },
  { type: 'date_signed', label: 'Date', defW: 105, defH: 22 },
];
const BLANK_W9_INFO = {
  taxpayerName: '',
  businessName: '',
  taxClassification: 'individual',
  tinType: 'ssn',
  tinFull: '',
  address: '',
  city: '',
  state: '',
  zip: '',
};
const W9_CLASSIFICATION_OPTIONS = [
  ['individual', 'Individual / Sole Proprietor'],
  ['llc', 'LLC'],
  ['s_corp', 'S Corporation'],
  ['c_corp', 'C Corporation'],
  ['partnership', 'Partnership'],
  ['trust', 'Trust / Estate'],
  ['other', 'Other'],
];

function cleanW9Prefill(source = {}, signerName = '') {
  return {
    ...BLANK_W9_INFO,
    taxpayerName: String(source.taxpayerName || source.name || signerName || '').trim(),
    businessName: String(source.businessName || '').trim(),
    taxClassification: W9_CLASSIFICATION_OPTIONS.some(([key]) => key === source.taxClassification)
      ? source.taxClassification
      : 'individual',
    tinType: source.tinType === 'ein' ? 'ein' : 'ssn',
    tinFull: String(source.tinFull || '').trim(),
    address: String(source.address || '').trim(),
    city: String(source.city || '').trim(),
    state: String(source.state || '').trim(),
    zip: String(source.zip || '').trim(),
  };
}

/**
 * SignDocument — public /sign/:token page.
 *
 * Loads the unsigned PDF + offer summary via /api/sign/get, shows
 * the doc in an iframe, captures a drawn or typed signature, and posts back
 * to /api/sign/submit with the typed name + consent flag. After
 * a successful submit it shows a confirmation screen.
 *
 * No auth — the URL token IS the auth. The endpoints validate that
 * the token resolves to an actionable request and reject otherwise.
 *
 * Compliance bits live in the API; this page just makes sure the
 * signer sees the consent text and explicitly opts in (checkbox)
 * + types their full legal name (intent) before the Sign button
 * unlocks.
 */
export default function SignDocument() {
  const { token } = useParams();
  const [load, setLoad] = useState({ state: 'loading', data: null, error: null });
  const [typedName, setTypedName] = useState('');
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [placedFields, setPlacedFields] = useState([]);
  const [wiringInfo, setWiringInfo] = useState({
    beneficiaryName: '',
    beneficiaryAddress: '',
    bankName: '',
    bankAddress: '',
    accountType: '',
    accountNumber: '',
    routingNumber: '',
    swift: '',
    intermediaryBank: '',
    reference: '',
  });
  // How this seller wants the money. Defaulted to a wire because that is
  // what the form was sent to collect — the other two are one tap away,
  // and the point of having them is that nobody gets stuck.
  const [payMethod, setPayMethod] = useState('wire');
  const [mailTo, setMailTo] = useState('');
  const [w9Info, setW9Info] = useState(BLANK_W9_INFO);
  // The Paraclete agent's own saved mark (Settings → My Signature).
  // Only ever fetched for the counter-signer: the seller half of this
  // page is unauthenticated, and there is nothing to fetch for them.
  const [savedSignature, setSavedSignature] = useState({ png: '', name: '', applied: false });

  const canvasRef = useRef(null);
  const signControlsRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  const prepareSignatureCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#0f172a';
    ctx.fillStyle = '#0f172a';
    return { canvas, ctx, rect };
  };

  // Load.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`/api/sign/get?token=${encodeURIComponent(token)}`);
        const j = await r.json().catch(() => ({}));
        if (!alive) return;
        if (!r.ok) { setLoad({ state: 'error', data: null, error: j?.error || `HTTP ${r.status}` }); return; }
        if (j.alreadySigned) { setLoad({ state: 'terminal', data: j, error: null }); return; }
        setLoad({ state: 'ready', data: j, error: null });
        setTypedName(j.signerName || '');
        setW9Info(cleanW9Prefill(j.w9Prefill || {}, j.signerName || ''));
      } catch (err) {
        if (alive) setLoad({ state: 'error', data: null, error: err?.message || 'Failed to load' });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    setPlacedFields([]);
  }, [load.data?.localId]);

  // Canvas drawing — pointer events for unified mouse + touch.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const prepared = prepareSignatureCanvas();
    if (!prepared) return;
    const { ctx } = prepared;

    const ptr = (e) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onDown = (e) => {
      e.preventDefault();
      drawing.current = true;
      const p = ptr(e);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    };
    const onMove = (e) => {
      if (!drawing.current) return;
      e.preventDefault();
      const p = ptr(e);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      setHasInk(true);
    };
    const onUp = (e) => {
      if (!drawing.current) return;
      e.preventDefault();
      drawing.current = false;
    };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointerleave', onUp);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointerleave', onUp);
    };
  }, [load.state]);

  const clearSig = () => {
    prepareSignatureCanvas();
    setHasInk(false);
    // Cleared on purpose — don't let the auto-apply put it straight
    // back on the next render.
    setSavedSignature((prev) => ({ ...prev, applied: true }));
  };

  /**
   * The counter-signer signs with the mark they already saved.
   *
   * A Paraclete agent counter-signing an offer had to draw their
   * signature with a mouse, on every document, having already saved one
   * under Settings → My Signature for the mailed copies. Same person,
   * same mark, drawn again worse each time.
   *
   * Only for the counter-signer. The seller side of this page is
   * unauthenticated by design — the URL token is the whole credential —
   * so there is no session to read a signature from, and /api/my-signature
   * is scoped to the caller with no id parameter, meaning this cannot
   * reach anybody else's mark.
   */
  useEffect(() => {
    if (load.state !== 'ready' || load.data?.role !== 'counter_signer') return undefined;
    let alive = true;
    (async () => {
      try {
        const r = await authedFetch('/api/my-signature');
        if (!r.ok) return;
        const j = await r.json().catch(() => ({}));
        if (!alive || !j?.hasSignature || !j.signaturePng) return;
        setSavedSignature({ png: j.signaturePng, name: j.signerName || '', applied: false });
      } catch {
        // Not signed in, or offline. The pad still works.
      }
    })();
    return () => { alive = false; };
  }, [load.state, load.data?.role]);

  // Applied once the canvas is on screen and sized. Kept separate from
  // the fetch because prepareSignatureCanvas measures the element, and
  // measuring it before the ready view has painted gives a zero-width
  // pad and a signature scaled to nothing.
  useEffect(() => {
    if (!savedSignature.png || savedSignature.applied) return;
    let alive = true;
    (async () => {
      const painted = await paintSavedSignature(savedSignature.png);
      if (!alive) return;
      setSavedSignature((prev) => ({ ...prev, applied: true }));
      if (painted && savedSignature.name) setTypedName((prev) => prev || savedSignature.name);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedSignature.png, savedSignature.applied]);

  const pasteTypedSignature = () => {
    const typed = typedName.trim();
    if (!typed) return;
    const prepared = prepareSignatureCanvas();
    if (!prepared) return;
    const { ctx, rect } = prepared;
    const maxWidth = Math.max(180, rect.width - 48);
    let fontSize = 46;
    do {
      ctx.font = `${fontSize}px "Brush Script MT", "Snell Roundhand", "Segoe Script", cursive`;
      fontSize -= 2;
    } while (ctx.measureText(typed).width > maxWidth && fontSize > 24);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(typed, rect.width / 2, rect.height / 2 + 4);
    setHasInk(true);
  };

  /**
   * Draw a saved signature image onto the pad.
   *
   * Scaled to fit rather than stretched: a signature squashed to the
   * pad's aspect ratio is a different mark from the one on file, and
   * the whole point of a saved signature is that it is always the same.
   */
  const paintSavedSignature = (dataUrl) => new Promise((resolve) => {
    const prepared = prepareSignatureCanvas();
    if (!prepared || !dataUrl) { resolve(false); return; }
    const { ctx, rect } = prepared;
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(rect.width / img.width, rect.height / img.height, 1);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (rect.width - w) / 2, (rect.height - h) / 2, w, h);
      setHasInk(true);
      resolve(true);
    };
    img.onerror = () => resolve(false);
    img.src = dataUrl;
  });

  const setWiringField = (key, value) => {
    setWiringInfo((prev) => ({ ...prev, [key]: value }));
  };

  const setW9Field = (key, value) => {
    setW9Info((prev) => ({ ...prev, [key]: value }));
  };

  const scrollToSigning = () => {
    signControlsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  /**
   * What is still missing, given how this seller wants to be paid.
   *
   * The form used to demand four bank fields of everybody, because it is
   * titled Wiring Instructions and that is what it asked for. A seller who
   * wanted a cheque could not submit it: the button stayed dead until they
   * typed bank details they had no intention of using. So they closed the
   * tab, and somebody at this end chased them for a fortnight.
   *
   * They can say "post me a cheque" now, and that answer is as complete as
   * any other.
   */
  const wiringMissing = (data) => {
    if (!data?.requestWiringInstructions) return [];
    if (payMethod === 'check') {
      return String(mailTo || '').trim() ? [] : ['Where to post the check'];
    }
    const required = [
      ['beneficiaryName', 'Beneficiary Name'],
      ['bankName', 'Bank Name'],
      ['accountNumber', 'Account Number'],
      ['routingNumber', 'Routing / ABA'],
    ];
    return required
      .filter(([key]) => !String(wiringInfo[key] || '').trim())
      .map(([, label]) => label);
  };

  const w9Missing = (data) => {
    if (!data?.requestW9Info) return [];
    const missing = [];
    if (!String(w9Info.taxpayerName || '').trim()) missing.push('Taxpayer name');
    if (!String(w9Info.address || '').trim()) missing.push('Address');
    if (!String(w9Info.city || '').trim()) missing.push('City');
    if (!String(w9Info.state || '').trim()) missing.push('State');
    if (!String(w9Info.zip || '').trim()) missing.push('ZIP');
    if (String(w9Info.tinFull || '').replace(/\D/g, '').length !== 9) {
      missing.push(w9Info.tinType === 'ein' ? '9-digit EIN' : '9-digit SSN');
    }
    return missing;
  };

  const onSubmit = async () => {
    if (!canvasRef.current) return;
    const needsSignerPlacement = !!(load.data?.isOnboarding || load.data?.allowSignerPlacedFields);
    const hasPlacedSignature = placedFields.some((f) => f.type === 'signature' || f.type === 'initials');
    const hasPlacedDate = placedFields.some((f) => f.type === 'date_signed');
    if (needsSignerPlacement && (!hasPlacedSignature || !hasPlacedDate)) {
      alert('Please place at least a Signature field and a Date field on the document before signing.');
      return;
    }
    const missingWiring = wiringMissing(load.data);
    if (missingWiring.length) {
      alert(`Please complete the requested wiring instructions: ${missingWiring.join(', ')}`);
      return;
    }
    const missingW9 = w9Missing(load.data);
    if (missingW9.length) {
      alert(`Please complete the requested W-9 information: ${missingW9.join(', ')}`);
      return;
    }
    if (!hasInk) { alert('Please draw your signature or click your typed signature to place it in the box.'); return; }
    if (!typedName.trim()) { alert('Please type your full legal name.'); return; }
    if (!consented) { alert('Please check the consent box to continue.'); return; }
    const dataUrl = canvasRef.current.toDataURL('image/png');
    setSubmitting(true);
    try {
      const r = await fetch('/api/sign/submit', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          token,
          signatureDataUrl: dataUrl,
          typedName: typedName.trim(),
          consented: true,
          ...(needsSignerPlacement ? { signerPlacedFields: placedFields } : {}),
          ...(load.data?.requestWiringInstructions
            ? { wiringInfo, paymentMethod: payMethod, mailTo: payMethod === 'check' ? mailTo : '' }
            : {}),
          ...(load.data?.requestW9Info ? { w9Info } : {}),
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
      setDone(true);
    } catch (err) {
      alert(`Could not submit signature: ${err?.message || 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  if (load.state === 'loading') return <CenterCard><p style={{ color: '#64748b' }}>Loading your document…</p></CenterCard>;
  if (load.state === 'error') return <CenterCard><h2>Could not open this sign link</h2><p style={{ color: '#b91c1c' }}>{load.error}</p></CenterCard>;
  if (load.state === 'terminal') {
    const s = load.data?.status;
    const role = load.data?.role;
    const headline = (() => {
      if (s === 'signed')   return 'Document fully signed.';
      if (s === 'declined') return 'Document was declined.';
      if (s === 'canceled') return 'Document was canceled.';
      if (s === 'queued')   return 'This form isn\'t ready yet.';
      if (s === 'awaiting_counter_sign' && role === 'seller') return 'Thanks — you\'ve signed.';
      if (s !== 'awaiting_counter_sign' && role === 'counter_signer') return 'Almost there.';
      return 'No action needed.';
    })();
    const body = (() => {
      if (s === 'queued') {
        return 'This document unlocks automatically once the offer is signed. You\'ll receive an email with this link the moment it\'s your turn — no action needed right now.';
      }
      if (s === 'awaiting_counter_sign' && role === 'seller') {
        return 'Your signature is captured. The buyer is now signing — you\'ll receive an email with the fully-executed copy when they finish.';
      }
      if (role === 'counter_signer' && s !== 'awaiting_counter_sign') {
        return 'The seller hasn\'t signed yet. You\'ll get a fresh notification when it\'s your turn.';
      }
      return 'You no longer need to sign. Contact the sender if you have questions.';
    })();
    return <CenterCard>
      <h2 style={{ margin: '0 0 0.5rem' }}>{headline}</h2>
      <p style={{ margin: 0, color: '#475569', lineHeight: 1.5 }}>{body}</p>
    </CenterCard>;
  }
  if (done) return <CenterCard>
    <div style={{ fontSize: '2.5rem', color: '#16a34a', lineHeight: 1 }}>✓</div>
    <h2 style={{ margin: '0.45rem 0 0.5rem', color: '#0f172a', fontSize: '1.35rem', fontWeight: 600 }}>You're all set.</h2>
    <p style={{ margin: 0, color: '#475569', lineHeight: 1.5, fontSize: '0.95rem' }}>
      Your signed copy has been delivered to the sender. You'll receive a fully-executed PDF by email shortly.
    </p>
  </CenterCard>;

  const data = load.data;
  const missingWiringFields = wiringMissing(data);
  const missingW9Fields = w9Missing(data);
  const needsSignerPlacement = !!(data?.isOnboarding || data?.allowSignerPlacedFields);
  const hasPlacedSignature = placedFields.some((f) => f.type === 'signature' || f.type === 'initials');
  const hasPlacedDate = placedFields.some((f) => f.type === 'date_signed');
  const missingPlacement = needsSignerPlacement && (!hasPlacedSignature || !hasPlacedDate);
  const signDisabled = submitting || !hasInk || !typedName.trim() || !consented || missingWiringFields.length > 0 || missingW9Fields.length > 0 || missingPlacement;
  return (
    <div className="sign-split-shell">
      <section className="sign-pdf-pane" aria-label="Document preview">
        <div className="sign-pdf-header">
          <div>
            <div className="sign-brand-eyebrow">Paraclete Oil Partners</div>
            <h1>{data.docTitle}</h1>
          </div>
          <div className="sign-signer-pill">
            <span>Signer</span>
            <strong>{data.signerName}</strong>
          </div>
        </div>
        <div className="sign-pdf-card">
          {needsSignerPlacement ? (
            <OnboardingPdfFieldPlacer
              pdfUrl={data.pdfUrl}
              fields={placedFields}
              onChange={setPlacedFields}
              typedName={typedName}
              viewerHeight="calc(100dvh - 128px)"
            />
          ) : (
            <PdfDocumentPreview
              pdfUrl={data.pdfUrl}
              fileName={data.fileName || data.docTitle || 'Offer document'}
            />
          )}
        </div>
        {!needsSignerPlacement && (
          <button type="button" className="sign-mobile-continue" onClick={scrollToSigning}>
            Continue to signing
          </button>
        )}
      </section>

      <aside ref={signControlsRef} className="sign-control-pane" aria-label="Signing controls">
        <div className="sign-control-scroll">
          <div className="sign-summary-card">
            <div className="sign-summary-kicker">Signature Request</div>
            <h2>{data.docTitle}</h2>
            <p>For <strong>{data.signerName}</strong></p>
            <p className="sign-summary-email">{data.signerEmail}</p>
          </div>

        {data.requestW9Info && (
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.1rem 1.15rem', boxShadow: '0 4px 14px rgba(2,6,23,0.05)', border: '1px solid #bfdbfe', textAlign: 'left', marginBottom: '1rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.05rem', color: '#1e3a8a' }}>W-9 Information</h2>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.86rem', lineHeight: 1.45 }}>
                Confirm these values. They will be placed into the address and taxpayer ID boxes on the signed W-9.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.85rem' }}>
              <W9Input label="Taxpayer Name" required value={w9Info.taxpayerName} onChange={(v) => setW9Field('taxpayerName', v)} />
              <W9Select
                label="Tax Classification"
                value={w9Info.taxClassification}
                onChange={(v) => setW9Field('taxClassification', v)}
                options={W9_CLASSIFICATION_OPTIONS}
              />
              <W9Input label="Business / DBA Name" value={w9Info.businessName} onChange={(v) => setW9Field('businessName', v)} fullWidth />
              <W9Input label="Address" required value={w9Info.address} onChange={(v) => setW9Field('address', v)} fullWidth />
              <W9Input label="City" required value={w9Info.city} onChange={(v) => setW9Field('city', v)} />
              <W9Input label="State" required value={w9Info.state} onChange={(v) => setW9Field('state', v.toUpperCase().slice(0, 2))} />
              <W9Input label="ZIP" required value={w9Info.zip} onChange={(v) => setW9Field('zip', v)} />
              <W9Select
                label="Taxpayer ID Type"
                value={w9Info.tinType}
                onChange={(v) => setW9Field('tinType', v)}
                options={[['ssn', 'Social Security Number'], ['ein', 'EIN']]}
              />
              <W9Input
                label={w9Info.tinType === 'ein' ? 'EIN' : 'Social Security Number'}
                required
                value={w9Info.tinFull}
                onChange={(v) => setW9Field('tinFull', v)}
              />
            </div>
            {missingW9Fields.length > 0 && (
              <p style={{ margin: '0.85rem 0 0', fontSize: '0.76rem', color: '#92400e', lineHeight: 1.45, textAlign: 'center', fontWeight: 600 }}>
                Missing: {missingW9Fields.join(', ')}
              </p>
            )}
          </div>
        )}

        {data.requestWiringInstructions && (
          <div style={{ background: '#fff', borderRadius: 8, padding: '1.1rem 1.15rem', boxShadow: '0 4px 14px rgba(2,6,23,0.05)', border: '1px solid #bbf7d0', textAlign: 'left', marginBottom: '1rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.05rem', color: '#064e3b' }}>How would you like to be paid?</h2>
              <p style={{ margin: 0, color: '#475569', fontSize: '0.86rem', lineHeight: 1.45 }}>
                Choose one. Anything you enter is saved securely to the closing file after you sign.
              </p>
            </div>
            {/* Three ways, and a cheque is one of them. This asked for
                bank details and nothing else, so a seller who wanted a
                cheque had no way to finish — the button stayed dead until
                they typed an account they did not want used. */}
            <div
              data-pay-method-choice
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                gap: '0.5rem',
                marginBottom: '1rem',
              }}
            >
              {[
                ['wire', 'Wire transfer', 'Straight to your bank on closing day'],
                ['ach', 'ACH', 'Bank transfer, usually a day or two'],
                ['check', 'Check by post', 'Posted to the address you give us'],
              ].map(([key, label, hint]) => {
                const picked = payMethod === key;
                return (
                  <button
                    key={key}
                    type="button"
                    data-pay-method={key}
                    onClick={() => setPayMethod(key)}
                    style={{
                      textAlign: 'left', padding: '0.6rem 0.7rem', cursor: 'pointer',
                      borderRadius: 8,
                      border: `1px solid ${picked ? '#047857' : '#cbd5e1'}`,
                      background: picked ? '#ecfdf5' : '#ffffff',
                      boxShadow: picked ? 'inset 0 0 0 1px #047857' : 'none',
                      font: 'inherit',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: picked ? '#064e3b' : '#0f172a' }}>
                      {label}
                    </div>
                    <div style={{ fontSize: '0.76rem', color: '#475569', marginTop: 2, lineHeight: 1.35 }}>
                      {hint}
                    </div>
                  </button>
                );
              })}
            </div>
            {payMethod === 'check' && (
              <label style={{ display: 'grid', gap: 4, marginBottom: '0.4rem' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155' }}>
                  Where should we post the check?
                </span>
                <textarea
                  data-pay-mail-to
                  rows={3}
                  value={mailTo}
                  onChange={(event) => setMailTo(event.target.value)}
                  placeholder={'Name\nStreet\nCity, State ZIP'}
                  style={{
                    padding: '0.55rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 8,
                    font: 'inherit', fontSize: '0.9rem', resize: 'vertical',
                  }}
                />
              </label>
            )}
            <div
              style={{
                display: payMethod === 'check' ? 'none' : 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '0.85rem',
              }}
            >
              <WiringInput label="Beneficiary Name" required value={wiringInfo.beneficiaryName} onChange={(v) => setWiringField('beneficiaryName', v)} />
              <WiringInput label="Beneficiary Address" value={wiringInfo.beneficiaryAddress} onChange={(v) => setWiringField('beneficiaryAddress', v)} />
              <WiringInput label="Bank Name" required value={wiringInfo.bankName} onChange={(v) => setWiringField('bankName', v)} />
              <WiringInput label="Bank Address" value={wiringInfo.bankAddress} onChange={(v) => setWiringField('bankAddress', v)} />
              <WiringInput label="Account Type" value={wiringInfo.accountType} onChange={(v) => setWiringField('accountType', v)} />
              <WiringInput label="Account Number" required value={wiringInfo.accountNumber} onChange={(v) => setWiringField('accountNumber', v)} />
              <WiringInput label="Routing / ABA" required value={wiringInfo.routingNumber} onChange={(v) => setWiringField('routingNumber', v)} />
              <WiringInput label="SWIFT / BIC" value={wiringInfo.swift} onChange={(v) => setWiringField('swift', v)} />
              <WiringInput label="Intermediary Bank" value={wiringInfo.intermediaryBank} onChange={(v) => setWiringField('intermediaryBank', v)} />
              <WiringInput label="Reference / Memo" value={wiringInfo.reference} onChange={(v) => setWiringField('reference', v)} fullWidth />
            </div>
            <p style={{ margin: '0.85rem 0 0', fontSize: '0.76rem', color: '#64748b', lineHeight: 1.45, textAlign: 'center' }}>
              Confirm these details are accurate. The buyer may verify wiring instructions by phone before sending funds.
            </p>
          </div>
        )}

        <div style={{ background: '#fff', borderRadius: 8, padding: '1.1rem 1.15rem', boxShadow: '0 4px 14px rgba(2,6,23,0.05)', border: '1px solid #e2e8f0', textAlign: 'center' }}>
          <h2 style={{ margin: '0 0 0.85rem', fontSize: '1.05rem' }}>Sign</h2>
          {Array.isArray(data.fields) && data.fields.length > 0 && (() => {
            const sigCount = data.fields.filter((f) => f.type === 'signature' || f.type === 'initials').length;
            const textCount = data.fields.filter((f) => f.type === 'text').length;
            const dateCount = data.fields.filter((f) => f.type === 'date_signed').length;
            const parts = [];
            if (sigCount) parts.push(`${sigCount} signature${sigCount > 1 ? 's' : ''}`);
            if (textCount) parts.push(`${textCount} name field${textCount > 1 ? 's' : ''}`);
            if (dateCount) parts.push(`${dateCount} date stamp${dateCount > 1 ? 's' : ''}`);
            if (parts.length === 0) return null;
            return (
              <div style={{
                background: '#eff6ff', border: '1px solid #bfdbfe',
                color: '#1e40af', borderRadius: 8,
                padding: '0.55rem 0.75rem', marginBottom: '0.85rem',
                fontSize: '0.78rem', lineHeight: 1.4,
                textAlign: 'center',
              }}>
                Your signature will be placed in {parts.join(', ')} on the document above. Sign once below — it will land in every spot.
              </div>
            );
          })()}

          {needsSignerPlacement && (
            <div style={{
              background: missingPlacement ? '#fff7ed' : '#ecfdf5',
              border: `1px solid ${missingPlacement ? '#fed7aa' : '#bbf7d0'}`,
              color: missingPlacement ? '#9a3412' : '#047857',
              borderRadius: 8,
              padding: '0.65rem 0.8rem',
              marginBottom: '0.85rem',
              fontSize: '0.78rem',
              lineHeight: 1.45,
              textAlign: 'center',
              fontWeight: 600,
            }}>
              Place the Signature and Date fields directly on the document above before signing.
            </div>
          )}

          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 4, textAlign: 'center' }}>Type your full legal name</label>
          <input
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            style={{ width: '100%', padding: '0.55rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.95rem', marginBottom: '1rem', boxSizing: 'border-box', outline: 'none', textAlign: 'center' }}
          />

          {typedName.trim() && (
            <button
              type="button"
              onClick={pasteTypedSignature}
              style={{
                width: '100%',
                border: '1px solid #bfdbfe',
                background: '#eff6ff',
                color: '#0f172a',
                borderRadius: 8,
                padding: '0.6rem 0.75rem',
                marginBottom: '1rem',
                cursor: 'pointer',
                fontFamily: '"Brush Script MT", "Snell Roundhand", "Segoe Script", cursive',
                fontSize: '1.65rem',
                lineHeight: 1.2,
              }}
              title="Place typed signature in the signature box"
            >
              {typedName.trim()}
            </button>
          )}

          {/* Says whose mark is in the box and where it came from, so an
              agent who did not expect a pre-filled signature is not left
              wondering who signed. Clear still wipes it. */}
          {savedSignature.png && hasInk && (
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.78rem', color: '#166534', textAlign: 'center' }}>
              Signed with your saved signature. Clear it to sign by hand instead.
            </p>
          )}

          <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569', marginBottom: 4, textAlign: 'center' }}>Signature</label>
          <div style={{ position: 'relative', border: '1px dashed #94a3b8', borderRadius: 8, background: '#fafbfc', overflow: 'hidden', marginBottom: '0.4rem' }}>
            <canvas
              ref={canvasRef}
              style={{ width: '100%', height: 160, display: 'block', touchAction: 'none', cursor: 'crosshair' }}
            />
            {!hasInk && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', color: '#94a3b8', fontSize: '0.85rem', fontStyle: 'italic' }}>
                Sign here
              </div>
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
            <button onClick={clearSig} type="button" style={{ background: 'transparent', border: 'none', color: '#475569', fontSize: '0.78rem', cursor: 'pointer', textDecoration: 'underline' }}>Clear</button>
          </div>

          {/* ESIGN consumer disclosure rendered as a scrollable
              gray panel above the consent checkbox. Splits the
              consentText paragraphs onto their own lines so the
              required disclosures (electronic record consent,
              system requirements, paper-copy availability,
              withdrawal procedure) read clearly. */}
          <div style={{
            maxWidth: 620, margin: '0 auto 12px',
            background: '#f8fafc', border: '1px solid #e2e8f0',
            borderRadius: 8, padding: '0.75rem 0.95rem',
            textAlign: 'left',
            fontSize: '0.78rem', lineHeight: 1.55, color: '#334155',
            maxHeight: 200, overflowY: 'auto',
          }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#0f172a', marginBottom: 6 }}>
              Electronic Signature Disclosure
            </div>
            {String(data.consentText || '').split(/\n\n+/).map((p, i) => (
              <p key={i} style={{ margin: i === 0 ? '0 0 8px' : '0 0 8px' }}>{p}</p>
            ))}
          </div>

          <label style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 10, fontSize: '0.85rem', fontWeight: 500, color: '#0f172a', cursor: 'pointer', marginBottom: '1rem', lineHeight: 1.5, textAlign: 'left', maxWidth: 620, marginLeft: 'auto', marginRight: 'auto' }}>
            <input
              type="checkbox"
              checked={consented}
              onChange={(e) => setConsented(e.target.checked)}
              style={{ marginTop: 3, accentColor: '#0f172a', flexShrink: 0 }}
            />
            <span>I have read the disclosure above and agree to sign electronically.</span>
          </label>

          <button
            onClick={onSubmit}
            disabled={signDisabled}
            style={{
              width: '100%', padding: '0.85rem 1.4rem',
              background: signDisabled ? '#cbd5e1' : '#0f172a',
              color: '#fff', border: 'none', borderRadius: 10,
              fontSize: '0.95rem', fontWeight: 600,
              cursor: signDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Signing…' : 'Sign Document'}
          </button>
        </div>
        </div>
      </aside>
    </div>
  );
}

function CenterCard({ children }) {
  return (
    <div style={{ background: '#f1f5f9', width: '100%', height: '100vh', minHeight: '100dvh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: 480, width: '100%', background: '#fff', color: '#0f172a', borderRadius: 12, padding: '2rem', textAlign: 'center', boxShadow: '0 12px 30px rgba(15,23,42,0.1)', border: '1px solid #e2e8f0' }}>
        {children}
      </div>
    </div>
  );
}

function WiringInput({ label, value, onChange, required = false, fullWidth = false }) {
  return (
    <label style={{ display: 'block', gridColumn: fullWidth ? '1 / -1' : undefined }}>
      <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 600, color: '#334155', marginBottom: 4 }}>
        {label}{required ? ' *' : ''}
      </span>
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        style={{
          width: '100%',
          padding: '0.58rem 0.72rem',
          border: '1px solid #cbd5e1',
          borderRadius: 8,
          fontSize: '0.9rem',
          color: '#0f172a',
          boxSizing: 'border-box',
          outline: 'none',
          background: '#ffffff',
        }}
      />
    </label>
  );
}

function W9Input({ label, value, onChange, required = false, fullWidth = false }) {
  return (
    <label style={{ display: 'block', gridColumn: fullWidth ? '1 / -1' : undefined }}>
      <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 600, color: '#334155', marginBottom: 4 }}>
        {label}{required ? ' *' : ''}
      </span>
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        style={{
          width: '100%',
          padding: '0.58rem 0.72rem',
          border: '1px solid #cbd5e1',
          borderRadius: 8,
          fontSize: '0.9rem',
          color: '#0f172a',
          boxSizing: 'border-box',
          outline: 'none',
          background: '#ffffff',
        }}
      />
    </label>
  );
}

function W9Select({ label, value, onChange, options = [] }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 600, color: '#334155', marginBottom: 4 }}>
        {label}
      </span>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          padding: '0.58rem 0.72rem',
          border: '1px solid #cbd5e1',
          borderRadius: 8,
          fontSize: '0.9rem',
          color: '#0f172a',
          boxSizing: 'border-box',
          outline: 'none',
          background: '#ffffff',
        }}
      >
        {options.map(([key, labelText]) => (
          <option key={key} value={key}>{labelText}</option>
        ))}
      </select>
    </label>
  );
}

/**
 * The size control that sits above a document. Both the offer the
 * signer reads and the onboarding page they drop fields onto use it.
 */
function SignZoomBar({ zoom, onChange }) {
  return (
    <div className="sign-pdf-zoombar">
      <span className="sign-pdf-zoom-label">Document size</span>
      <div className="sign-pdf-zoom-group" role="group" aria-label="Document zoom">
        <button
          type="button"
          className="sign-pdf-zoom-btn"
          onClick={() => onChange(stepSignZoom(zoom, -1))}
          disabled={zoom === SIGN_ZOOM_STEPS[0]}
          aria-label="Make the document smaller"
        >
          &minus;
        </button>
        <span className="sign-pdf-zoom-value" aria-live="polite">{Math.round(zoom * 100)}%</span>
        <button
          type="button"
          className="sign-pdf-zoom-btn"
          onClick={() => onChange(stepSignZoom(zoom, 1))}
          disabled={zoom === SIGN_ZOOM_STEPS[SIGN_ZOOM_STEPS.length - 1]}
          aria-label="Make the document bigger"
        >
          +
        </button>
      </div>
      <button
        type="button"
        className="sign-pdf-zoom-reset"
        onClick={() => onChange(1)}
        disabled={zoom === 1}
      >
        Fit to screen
      </button>
    </div>
  );
}

function PdfDocumentPreview({ pdfUrl, fileName }) {
  const [pages, setPages] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [zoom, setZoom] = useState(1);
  const canvasRefs = useRef({});
  const renderTasks = useRef({});
  const scrollRef = useRef(null);
  const anchorRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setPages([]);
    setLoadError('');
    canvasRefs.current = {};
    if (!pdfUrl) return () => { cancelled = true; };
    (async () => {
      try {
        const r = await fetch(pdfUrl);
        if (!r.ok) throw new Error(`PDF fetch failed (${r.status})`);
        const bytes = new Uint8Array(await r.arrayBuffer());
        const { getDocument } = await loadPdfjs();
        const pdf = await getDocument({ data: bytes, wasmUrl: '/pdfjs/' }).promise;
        const out = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          out.push({ pageNum: i, page: await pdf.getPage(i) });
        }
        if (!cancelled) setPages(out);
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || 'Could not load the offer document.');
      }
    })();
    return () => { cancelled = true; };
  }, [pdfUrl]);

  // Zooming redraws the pages rather than stretching the picture we
  // already have. signZoom.js carries the scale and the memory
  // ceiling behind it.
  const renderScale = signRenderScale(zoom, pages.length);

  useEffect(() => {
    let cancelled = false;
    const tasks = renderTasks.current;
    (async () => {
      for (const p of pages) {
        if (cancelled) return;
        const canvas = canvasRefs.current[p.pageNum];
        if (!canvas) continue;
        // A second zoom click can land while this pass is still
        // drawing, and pdf.js refuses two renders of one page at once.
        // Cancel the one in flight and wait for it to let go.
        const previous = tasks[p.pageNum];
        if (previous) {
          previous.cancel();
          try { await previous.promise; } catch { /* cancelled, as asked */ }
          if (cancelled) return;
        }
        const viewport = p.page.getViewport({ scale: renderScale });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext('2d');
        const task = p.page.render({ canvasContext: ctx, viewport, canvas });
        tasks[p.pageNum] = task;
        try {
          await task.promise;
        } catch {
          // pdf.js cancels renders during fast reloads; harmless.
        }
        if (tasks[p.pageNum] === task) delete tasks[p.pageNum];
      }
    })();
    return () => { cancelled = true; };
  }, [pages, renderScale]);

  // Keep the signer looking at the same part of the page across a zoom
  // change. Without this, every click throws them back to the top and
  // they lose the clause they were reading.
  const changeZoom = (next) => {
    const el = scrollRef.current;
    if (el && el.scrollHeight > el.clientHeight) {
      anchorRef.current = (el.scrollTop + el.clientHeight / 2) / el.scrollHeight;
    }
    setZoom(next);
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const ratio = anchorRef.current;
    anchorRef.current = null;
    if (!el || ratio == null) return;
    el.scrollTop = Math.max(0, (ratio * el.scrollHeight) - (el.clientHeight / 2));
  }, [zoom]);

  return (
    <div className="sign-pdf-viewer">
      <SignZoomBar zoom={zoom} onChange={changeZoom} />

      <div
        ref={scrollRef}
        className={`sign-pdf-preview${zoom > 1 ? ' is-zoomed' : ''}`}
        style={{ '--sign-zoom': zoom }}
        aria-label="Offer document"
      >
        {loadError && (
          <div className="sign-pdf-preview-state sign-pdf-preview-error">
            <strong>Could not show the offer automatically.</strong>
            <span>{loadError}</span>
            {pdfUrl && <a href={pdfUrl} target="_blank" rel="noreferrer">Open PDF in a new tab</a>}
          </div>
        )}
        {!loadError && pages.length === 0 && (
          <div className="sign-pdf-preview-state">
            Loading offer...
          </div>
        )}
        {pages.map((p) => (
          <div key={p.pageNum} className="sign-pdf-preview-page">
            <canvas
              ref={(el) => { if (el) canvasRefs.current[p.pageNum] = el; }}
              aria-label={`${fileName || 'Offer document'} page ${p.pageNum}`}
            />
            <div className="sign-pdf-preview-page-label">Page {p.pageNum}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OnboardingPdfFieldPlacer({ pdfUrl, fields, onChange, typedName, viewerHeight = '70vh' }) {
  const [pages, setPages] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [tool, setTool] = useState('signature');
  const [selectedId, setSelectedId] = useState(null);
  const [drag, setDrag] = useState(null);
  const [zoom, setZoom] = useState(1);
  const canvasRefs = useRef({});
  const renderTasks = useRef({});
  const scrollRef = useRef(null);
  const anchorRef = useRef(null);

  // Pixels per PDF point on screen. Every conversion in this view goes
  // through it, so what gets stored stays in points and the zoom never
  // reaches the server.
  const viewScale = placementViewScale(zoom);
  const renderScale = Math.min(viewScale, signRenderCeiling(pages.length));

  useEffect(() => {
    let cancelled = false;
    setPages([]);
    setLoadError('');
    canvasRefs.current = {};
    if (!pdfUrl) return () => { cancelled = true; };
    (async () => {
      try {
        const r = await fetch(pdfUrl);
        if (!r.ok) throw new Error(`PDF fetch failed (${r.status})`);
        const bytes = new Uint8Array(await r.arrayBuffer());
        const { getDocument } = await loadPdfjs();
        const pdf = await getDocument({ data: bytes, wasmUrl: '/pdfjs/' }).promise;
        const out = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          // The page's own size, in points — the units the fields are
          // stored and stamped in.
          const unscaled = page.getViewport({ scale: 1 });
          out.push({ pageNum: i, page, widthPt: unscaled.width, heightPt: unscaled.height });
        }
        if (!cancelled) setPages(out);
      } catch (err) {
        if (!cancelled) setLoadError(err?.message || 'Could not load PDF for field placement.');
      }
    })();
    return () => { cancelled = true; };
  }, [pdfUrl]);

  useEffect(() => {
    let cancelled = false;
    const tasks = renderTasks.current;
    (async () => {
      for (const p of pages) {
        if (cancelled) return;
        const canvas = canvasRefs.current[p.pageNum];
        if (!canvas) continue;
        // pdf.js refuses two renders of one page at once, and a second
        // zoom click lands long before the first pass is done.
        const previous = tasks[p.pageNum];
        if (previous) {
          previous.cancel();
          try { await previous.promise; } catch { /* cancelled, as asked */ }
          if (cancelled) return;
        }
        const viewport = p.page.getViewport({ scale: renderScale });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext('2d');
        const task = p.page.render({ canvasContext: ctx, viewport, canvas });
        tasks[p.pageNum] = task;
        try {
          await task.promise;
        } catch {
          // pdf.js cancels renders during fast reloads; harmless.
        }
        if (tasks[p.pageNum] === task) delete tasks[p.pageNum];
      }
    })();
    return () => { cancelled = true; };
  }, [pages, renderScale]);

  useEffect(() => {
    if (!drag) return undefined;
    const onMove = (e) => {
      onChange((prev) => prev.map((f) => {
        if (f.id !== drag.id) return f;
        const page = pages.find((p) => p.pageNum === f.page);
        const maxW = page ? page.widthPt : 612;
        const maxH = page ? page.heightPt : 792;
        const geom = {
          origin: { x: drag.ox, y: drag.oy, width: drag.ow, height: drag.oh },
          dxPx: e.clientX - drag.startX,
          dyPx: e.clientY - drag.startY,
          viewScale,
          maxW,
          maxH,
        };
        if (drag.mode === 'resize') {
          return { ...f, ...resizedFieldBox({ ...geom, x: f.x, y: f.y }) };
        }
        return { ...f, ...movedFieldBox({ ...geom, width: f.width, height: f.height }) };
      }));
    };
    const onUp = () => setDrag(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [drag, onChange, pages, viewScale]);

  // Keep the signer looking at the same part of the page across a zoom
  // change, so they do not lose the line they were aiming at.
  const changeZoom = (next) => {
    const el = scrollRef.current;
    if (el && el.scrollHeight > el.clientHeight) {
      anchorRef.current = (el.scrollTop + el.clientHeight / 2) / el.scrollHeight;
    }
    setZoom(next);
  };

  useLayoutEffect(() => {
    const el = scrollRef.current;
    const ratio = anchorRef.current;
    anchorRef.current = null;
    if (!el || ratio == null) return;
    el.scrollTop = Math.max(0, (ratio * el.scrollHeight) - (el.clientHeight / 2));
  }, [zoom]);

  const addField = (pageNum, e) => {
    if (e.target !== e.currentTarget) return;
    const meta = ONBOARDING_FIELD_TYPES.find((t) => t.type === tool);
    if (!meta) return;
    const page = pages.find((p) => p.pageNum === pageNum);
    const rect = e.currentTarget.getBoundingClientRect();
    const id = `sp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const box = newFieldBox({
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      viewScale,
      defW: meta.defW,
      defH: meta.defH,
      maxW: page ? page.widthPt : rect.width / viewScale,
      maxH: page ? page.heightPt : rect.height / viewScale,
    });
    onChange((prev) => [...prev, { id, type: meta.type, page: pageNum, ...box, signer: 0 }]);
    setSelectedId(id);
  };

  const removeField = (id) => {
    onChange((prev) => prev.filter((f) => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  return (
    <div style={{ background: '#e2e8f0' }}>
      <div style={{
        padding: '0.65rem 0.75rem',
        borderBottom: '1px solid #cbd5e1',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: '0.74rem', color: '#475569', fontWeight: 600, marginRight: 4 }}>
          Click a tool, then click the exact spot on the PDF:
        </span>
        {ONBOARDING_FIELD_TYPES.map((ft) => {
          const active = tool === ft.type;
          return (
            <button
              key={ft.type}
              type="button"
              onClick={() => setTool(ft.type)}
              style={{
                padding: '0.38rem 0.65rem',
                border: `1px solid ${active ? '#0f172a' : '#cbd5e1'}`,
                background: active ? '#0f172a' : '#ffffff',
                color: active ? '#ffffff' : '#0f172a',
                borderRadius: 8,
                fontSize: '0.76rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {ft.label}
            </button>
          );
        })}
        <span style={{ fontSize: '0.72rem', color: '#64748b', marginLeft: 4 }}>
          {fields.length} placed
        </span>
      </div>

      <SignZoomBar zoom={zoom} onChange={changeZoom} />

      <div
        ref={scrollRef}
        style={{
          height: viewerHeight,
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          // Centring a page wider than the box puts its left edge out
          // of reach of the scrollbar.
          alignItems: zoom > 1 ? 'flex-start' : 'center',
          gap: '1.35rem',
        }}
      >
        {loadError && (
          <div style={{ color: '#b91c1c', background: '#fff', border: '1px solid #fecaca', padding: '0.8rem 1rem', borderRadius: 8 }}>
            {loadError}
          </div>
        )}
        {!loadError && pages.length === 0 && (
          <div style={{ color: '#475569', background: '#fff', border: '1px solid #cbd5e1', padding: '0.8rem 1rem', borderRadius: 8 }}>
            Loading document placement view...
          </div>
        )}
        {pages.map((p) => (
          <div
            key={p.pageNum}
            style={{
              position: 'relative',
              flex: '0 0 auto',
              width: p.widthPt * viewScale,
              height: p.heightPt * viewScale,
              background: '#ffffff',
              boxShadow: '0 6px 18px rgba(15,23,42,0.22)',
            }}
          >
            <canvas
              ref={(el) => { if (el) canvasRefs.current[p.pageNum] = el; }}
              style={{ display: 'block', width: '100%', height: '100%' }}
            />
            <div
              onPointerDown={(e) => addField(p.pageNum, e)}
              style={{ position: 'absolute', inset: 0, cursor: 'crosshair' }}
            >
              {fields.filter((f) => f.page === p.pageNum).map((f) => {
                const selected = selectedId === f.id;
                const label = f.type === 'signature'
                  ? 'Signature'
                  : f.type === 'date_signed'
                    ? new Date().toLocaleDateString()
                    : (typedName || 'Typed Name');
                return (
                  <div
                    key={f.id}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      e.currentTarget.setPointerCapture?.(e.pointerId);
                      setSelectedId(f.id);
                      setDrag({
                        id: f.id,
                        mode: 'move',
                        startX: e.clientX,
                        startY: e.clientY,
                        ox: f.x,
                        oy: f.y,
                        ow: f.width,
                        oh: f.height,
                      });
                    }}
                    style={{
                      position: 'absolute',
                      left: f.x * viewScale,
                      top: f.y * viewScale,
                      width: f.width * viewScale,
                      height: f.height * viewScale,
                      background: 'rgba(37,99,235,0.13)',
                      border: '2px solid #2563eb',
                      color: '#1d4ed8',
                      boxShadow: selected ? '0 0 0 2px #fff, 0 0 0 4px #2563eb' : 'none',
                      borderRadius: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      userSelect: 'none',
                      cursor: 'move',
                      fontSize: f.type === 'signature' ? `${1 * zoom}rem` : `${0.72 * zoom}rem`,
                      fontFamily: f.type === 'signature' ? 'Brush Script MT, Snell Roundhand, Segoe Script, cursive' : 'inherit',
                      fontWeight: f.type === 'signature' ? 400 : 800,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                    {selected && (
                      <>
                        <button
                          type="button"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); removeField(f.id); }}
                          title="Remove field"
                          style={{
                            position: 'absolute',
                            top: -10,
                            right: -10,
                            width: 22,
                            height: 22,
                            borderRadius: 999,
                            border: '1px solid #2563eb',
                            background: '#ffffff',
                            color: '#1d4ed8',
                            cursor: 'pointer',
                            fontSize: 14,
                            lineHeight: '18px',
                            padding: 0,
                          }}
                        >
                          x
                        </button>
                        <div
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            setDrag({
                              id: f.id,
                              mode: 'resize',
                              startX: e.clientX,
                              startY: e.clientY,
                              ox: f.x,
                              oy: f.y,
                              ow: f.width,
                              oh: f.height,
                            });
                          }}
                          style={{
                            position: 'absolute',
                            right: -6,
                            bottom: -6,
                            width: 12,
                            height: 12,
                            background: '#ffffff',
                            border: '2px solid #2563eb',
                            borderRadius: 2,
                            cursor: 'nwse-resize',
                          }}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            <div style={{ position: 'absolute', left: 0, bottom: -21, color: '#475569', fontSize: '0.68rem', fontWeight: 600 }}>
              Page {p.pageNum}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
